/**
 * AssistentGlobalPage - Globaler KI-Assistent
 * WhatsApp-Style Chat mit Streaming, PDF-Upload, Schnellbefehle.
 * Claude Sonnet 4 via /api/chat/stream (SSE).
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send,
  Plus,
  Paperclip,
  FileText,
  Clock,
  Trash2,
  Archive,
  ArchiveRestore,
  ChevronRight,
  Loader2,
  BookOpen,
  ClipboardList,
  Scale,
  CalendarClock,
  Save,
  User,
  MessageSquarePlus,
  History,
  X,
  Upload,
  AlertCircle
} from 'lucide-react';
import ZeoIcon from '@/components/ZeoIcon';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useTabs } from '@/contexts/TabContext';
import ReactMarkdown from 'react-markdown';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const SCHNELLBEFEHLE = [
  { icon: BookOpen, label: 'Akte zusammenfassen', cmd: 'Fasse die aktuelle Akte zusammen.' },
  { icon: CalendarClock, label: 'Fristen prüfen', cmd: 'Welche Fristen laufen in den nächsten 14 Tagen ab?' },
  { icon: FileText, label: 'Brief schreiben', cmd: 'Schreibe einen Brief an das Betreuungsgericht.' },
  { icon: ClipboardList, label: 'Jahresbericht', cmd: 'Erstelle den Jahresbericht für den aktuell geöffneten Betreuten.' },
];

export default function AssistentGlobalPage() {
  const { openTab } = useTabs();
  const [sessions, setSessions] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [showSidebar, setShowSidebar] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [betreuterId, setBetreuterId] = useState(null);
  const [betreuerName, setBetreuerName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [klienten, setKlienten] = useState([]);
  const [showKlientenSelect, setShowKlientenSelect] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const abortRef = useRef(null);

  // Scroll to bottom on new messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => scrollToBottom(), [messages, streamText, scrollToBottom]);

  // Load sessions
  useEffect(() => {
    loadSessions();
    loadKlienten();
  }, []);

  const loadSessions = async (archived = false) => {
    try {
      const res = await fetch(`${API_URL}/api/chat/sessions?archiviert=${archived}`);
      if (res.ok) setSessions(await res.json());
    } catch (e) { console.error('Sessions laden fehlgeschlagen:', e); }
  };

  const loadKlienten = async () => {
    try {
      const res = await fetch(`${API_URL}/api/betreuung/klienten?limit=50`);
      if (res.ok) {
        const data = await res.json();
        setKlienten(Array.isArray(data) ? data : data.klienten || []);
      }
    } catch (e) { console.error(e); }
  };

  const loadSession = async (sessionId) => {
    try {
      const res = await fetch(`${API_URL}/api/chat/sessions/${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        setActiveSession(data);
        setMessages(data.messages || []);
        if (data.betreuter_id) setBetreuterId(data.betreuter_id);
      }
    } catch (e) { console.error(e); }
  };

  const startNewSession = () => {
    setActiveSession(null);
    setMessages([]);
    setStreamText('');
    setBetreuterId(null);
    setBetreuerName('');
    inputRef.current?.focus();
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm('Chat endgültig löschen? (Archivieren bewahrt das Wissen)')) return;
    try {
      await fetch(`${API_URL}/api/chat/sessions/${sessionId}`, { method: 'DELETE' });
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (activeSession?.id === sessionId) startNewSession();
      toast.success('Chat gelöscht');
    } catch (e) { toast.error('Fehler beim Löschen'); }
  };

  const archiveSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API_URL}/api/chat/sessions/${sessionId}/archiv`, { method: 'PUT' });
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (activeSession?.id === sessionId) startNewSession();
      toast.success('Chat archiviert');
    } catch (e) { toast.error('Fehler beim Archivieren'); }
  };

  const unarchiveSession = async (sessionId, e) => {
    e.stopPropagation();
    try {
      await fetch(`${API_URL}/api/chat/sessions/${sessionId}/unarchiv`, { method: 'PUT' });
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      toast.success('Chat wiederhergestellt');
    } catch (e) { toast.error('Fehler'); }
  };

  const sendMessage = async (text = null) => {
    const nachricht = text || input.trim();
    if (!nachricht && !uploadFile) return;
    if (isStreaming) return;

    const userMsg = {
      id: Date.now().toString(),
      rolle: 'user',
      text: nachricht,
      timestamp: new Date().toISOString(),
      dateiname: uploadFile?.name,
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsStreaming(true);
    setStreamText('');

    try {
      let response;
      const sessionId = activeSession?.id || undefined;

      if (uploadFile) {
        // FormData for file upload
        const formData = new FormData();
        formData.append('nachricht', nachricht);
        if (sessionId) formData.append('session_id', sessionId);
        if (betreuterId) formData.append('betreuter_id', betreuterId);
        formData.append('datei', uploadFile);

        response = await fetch(`${API_URL}/api/chat/stream-mit-datei`, {
          method: 'POST',
          body: formData,
        });
        setUploadFile(null);
      } else {
        response = await fetch(`${API_URL}/api/chat/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            nachricht,
            session_id: sessionId,
            betreuter_id: betreuterId,
          }),
        });
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = '';
      let newSessionId = sessionId;
      let messageId = null;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));

            if (data.type === 'session') {
              newSessionId = data.session_id;
              if (data.betreuter_name) setBetreuerName(data.betreuter_name);
            } else if (data.type === 'content') {
              fullText += data.text;
              setStreamText(fullText);
            } else if (data.type === 'tool_call') {
              setStreamText(prev => prev + `\n> Rufe Tool auf: **${data.tool}**...\n`);
            } else if (data.type === 'tool_result') {
              setStreamText(prev => prev + `> Tool ${data.tool}: ${data.success ? 'Erfolgreich' : 'Fehler'}\n\n`);
            } else if (data.type === 'done') {
              messageId = data.message_id;
            } else if (data.type === 'error') {
              fullText += `\n\n**Fehler:** ${data.content}`;
              setStreamText(fullText);
            }
          } catch (parseErr) { /* ignore non-JSON lines */ }
        }
      }

      // Add assistant message
      const assistantMsg = {
        id: messageId || Date.now().toString(),
        rolle: 'assistent',
        text: fullText,
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, assistantMsg]);
      setStreamText('');

      // Update session
      if (newSessionId && !activeSession) {
        setActiveSession({ id: newSessionId, titel: nachricht.slice(0, 60) });
      }
      loadSessions();

    } catch (error) {
      console.error('Chat-Fehler:', error);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        rolle: 'assistent',
        text: `**Fehler:** ${error.message}. Bitte versuchen Sie es erneut.`,
        timestamp: new Date().toISOString(),
        isError: true,
      }]);
      setStreamText('');
    } finally {
      setIsStreaming(false);
    }
  };

  const saveToAkte = async (text) => {
    if (!betreuterId) {
      setShowKlientenSelect(true);
      toast.info('Bitte wählen Sie zuerst einen Betreuten aus.');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/chat/save-to-akte`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          betreuter_id: betreuterId,
          text,
          titel: `KI-Assistent: ${text.slice(0, 50)}...`,
        }),
      });
      if (res.ok) toast.success('In Akte gespeichert');
      else toast.error('Fehler beim Speichern');
    } catch (e) { toast.error('Fehler beim Speichern'); }
  };

  const selectKlient = (k) => {
    setBetreuterId(k.id);
    setBetreuerName(`${k.vorname || ''} ${k.nachname || ''}`.trim());
    setShowKlientenSelect(false);
    toast.success(`Kontext: ${k.vorname} ${k.nachname}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex h-[calc(100vh-120px)] bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="assistent-global-page">
      {/* Sidebar: Sessions */}
      {showSidebar && (
        <div className="w-72 border-r border-slate-200 flex flex-col bg-slate-50/50" data-testid="chat-sidebar">
          <div className="p-3 border-b border-slate-200 space-y-2">
            <Button onClick={startNewSession} className="w-full" size="sm" data-testid="new-session-btn">
              <MessageSquarePlus className="w-4 h-4 mr-2" /> Neue Unterhaltung
            </Button>
            <button
              onClick={() => { const next = !showArchived; setShowArchived(next); loadSessions(next); }}
              className={`w-full text-xs px-2 py-1.5 rounded flex items-center justify-center gap-1.5 transition-colors ${showArchived ? 'bg-amber-100 text-amber-700' : 'hover:bg-slate-100 text-slate-500'}`}
              data-testid="toggle-archive-btn"
            >
              <Archive className="w-3.5 h-3.5" />
              {showArchived ? 'Aktive Chats anzeigen' : 'Archiv anzeigen'}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {sessions.length === 0 ? (
              <div className="text-center py-8 text-slate-400 text-sm">
                <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                {showArchived ? 'Kein archivierter Chat' : 'Keine Unterhaltungen'}
              </div>
            ) : (
              sessions.map(s => (
                <div
                  key={s.id}
                  className={`group flex items-center justify-between px-3 py-2.5 cursor-pointer border-b border-slate-100 hover:bg-slate-100 transition-colors ${
                    activeSession?.id === s.id ? 'bg-blue-50 border-l-2 border-l-blue-500' : ''
                  }`}
                  onClick={() => loadSession(s.id)}
                  data-testid={`session-${s.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{s.titel || 'Unterhaltung'}</p>
                    <p className="text-xs text-slate-400">
                      {s.updated_date ? new Date(s.updated_date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                    {showArchived ? (
                      <button onClick={(e) => unarchiveSession(s.id, e)} className="p-1 hover:bg-blue-100 rounded" title="Wiederherstellen">
                        <ArchiveRestore className="w-3.5 h-3.5 text-slate-400 hover:text-blue-500" />
                      </button>
                    ) : (
                      <button onClick={(e) => archiveSession(s.id, e)} className="p-1 hover:bg-amber-100 rounded" title="Archivieren">
                        <Archive className="w-3.5 h-3.5 text-slate-400 hover:text-amber-500" />
                      </button>
                    )}
                    <button onClick={(e) => deleteSession(s.id, e)} className="p-1 hover:bg-red-100 rounded" title="Löschen">
                      <Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-white">
          <div className="flex items-center gap-3">
            <button onClick={() => setShowSidebar(!showSidebar)} className="p-1.5 hover:bg-slate-100 rounded-lg">
              <History className="w-4 h-4 text-slate-500" />
            </button>
            <div>
              <h2 className="font-semibold text-slate-800 text-sm">Zeo - Olympus KI</h2>
              <p className="text-xs text-slate-400">Claude Sonnet 4 · Betreuungsrecht</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {betreuerName ? (
              <Badge variant="secondary" className="flex items-center gap-1 cursor-pointer" onClick={() => setShowKlientenSelect(!showKlientenSelect)} data-testid="active-context-badge">
                <User className="w-3 h-3" /> Akte: {betreuerName}
                <X className="w-3 h-3 ml-1" onClick={(e) => { e.stopPropagation(); setBetreuterId(null); setBetreuerName(''); }} />
              </Badge>
            ) : (
              <Button variant="outline" size="sm" onClick={() => setShowKlientenSelect(!showKlientenSelect)} data-testid="select-klient-btn">
                <User className="w-3.5 h-3.5 mr-1" /> Akte wählen
              </Button>
            )}
          </div>
        </div>

        {/* Klienten-Auswahl Dropdown */}
        {showKlientenSelect && (
          <div className="absolute right-4 top-[160px] z-50 bg-white border border-slate-200 rounded-lg shadow-xl w-80 max-h-64 overflow-y-auto" data-testid="klient-select-dropdown">
            <div className="p-2 border-b">
              <p className="text-xs font-medium text-slate-500">Betreuten-Akte als Kontext wählen:</p>
            </div>
            {klienten.map(k => (
              <div
                key={k.id}
                className="px-3 py-2 hover:bg-blue-50 cursor-pointer flex items-center justify-between text-sm"
                onClick={() => selectKlient(k)}
              >
                <span className="font-medium">{k.vorname} {k.nachname}</span>
                <Badge variant="outline" className="text-xs">{k.status || 'aktiv'}</Badge>
              </div>
            ))}
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="messages-area">
          {messages.length === 0 && !streamText && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500/10 to-indigo-600/10 flex items-center justify-center mb-4">
                <ZeoIcon size={56} />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-1">Hallo, ich bin Zeo!</h3>
              <p className="text-sm text-slate-400 max-w-md mb-6">
                Wie kann ich dir helfen? Stell mir eine Frage oder nutze die Schnellbefehle.
              </p>
              {/* Schnellbefehle */}
              <div className="grid grid-cols-2 gap-2 max-w-lg" data-testid="schnellbefehle">
                {SCHNELLBEFEHLE.map((cmd, i) => (
                  <button
                    key={i}
                    className="flex items-center gap-2 px-4 py-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 transition-all text-left text-sm group"
                    onClick={() => sendMessage(cmd.cmd)}
                    data-testid={`schnellbefehl-${i}`}
                  >
                    <cmd.icon className="w-4 h-4 text-slate-400 group-hover:text-blue-500 shrink-0" />
                    <span className="text-slate-600 group-hover:text-blue-700">{cmd.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Message Bubbles */}
          {messages.map((msg, idx) => (
            <div key={msg.id || idx} className={`flex ${msg.rolle === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[75%] ${msg.rolle === 'user' ? 'order-2' : 'order-1'}`}>
                {/* Avatar + Name */}
                <div className={`flex items-center gap-1.5 mb-1 ${msg.rolle === 'user' ? 'justify-end' : ''}`}>
                  {msg.rolle !== 'user' && (
                    <div className="w-5 h-5 rounded-full flex items-center justify-center">
                      <ZeoIcon size={20} />
                    </div>
                  )}
                  <span className="text-[10px] text-slate-400">
                    {msg.rolle === 'user' ? 'Sie' : 'Assistent'} · {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
                {/* Bubble */}
                <div className={`rounded-2xl px-4 py-2.5 ${
                  msg.rolle === 'user'
                    ? 'bg-blue-600 text-white rounded-br-md'
                    : msg.isError
                    ? 'bg-red-50 text-red-800 border border-red-200 rounded-bl-md'
                    : 'bg-slate-100 text-slate-800 rounded-bl-md'
                }`}>
                  {msg.dateiname && (
                    <div className="flex items-center gap-1.5 mb-1.5 text-xs opacity-80">
                      <Paperclip className="w-3 h-3" /> {msg.dateiname}
                    </div>
                  )}
                  {msg.rolle === 'user' ? (
                    <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                  ) : (
                    <div className="text-sm prose prose-sm max-w-none prose-slate">
                      <ReactMarkdown>{msg.text}</ReactMarkdown>
                    </div>
                  )}
                </div>
                {/* Save to Akte button */}
                {msg.rolle === 'assistent' && !msg.isError && (
                  <div className="flex justify-start mt-1">
                    <button
                      className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-blue-600 transition-colors px-2 py-0.5 rounded hover:bg-blue-50"
                      onClick={() => saveToAkte(msg.text)}
                      data-testid={`save-to-akte-${msg.id}`}
                    >
                      <Save className="w-3 h-3" /> In Akte speichern
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Streaming indicator */}
          {isStreaming && streamText && (
            <div className="flex justify-start">
              <div className="max-w-[75%]">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center">
                    <ZeoIcon size={20} />
                  </div>
                  <span className="text-[10px] text-slate-400">Assistent · schreibt...</span>
                </div>
                <div className="bg-slate-100 text-slate-800 rounded-2xl rounded-bl-md px-4 py-2.5">
                  <div className="text-sm prose prose-sm max-w-none prose-slate">
                    <ReactMarkdown>{streamText}</ReactMarkdown>
                  </div>
                  <span className="inline-block w-2 h-4 bg-blue-500 animate-pulse ml-0.5 rounded-sm" />
                </div>
              </div>
            </div>
          )}

          {isStreaming && !streamText && (
            <div className="flex justify-start">
              <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                <span className="text-sm text-slate-500">Denkt nach...</span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* File Upload Preview */}
        {uploadFile && (
          <div className="px-4 py-2 border-t border-slate-100 bg-blue-50/50 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <FileText className="w-4 h-4" />
              <span className="font-medium">{uploadFile.name}</span>
              <span className="text-xs text-blue-400">({(uploadFile.size / 1024).toFixed(0)} KB)</span>
            </div>
            <button onClick={() => setUploadFile(null)} className="p-1 hover:bg-blue-100 rounded">
              <X className="w-4 h-4 text-blue-500" />
            </button>
          </div>
        )}

        {/* Input Area */}
        <div className="px-4 py-3 border-t border-slate-200 bg-white" data-testid="chat-input-area">
          <div className="flex items-end gap-2">
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept=".pdf,.txt,.doc,.docx,.png,.jpg,.jpeg"
              onChange={(e) => {
                if (e.target.files?.[0]) setUploadFile(e.target.files[0]);
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 h-10 w-10 p-0"
              onClick={() => fileInputRef.current?.click()}
              disabled={isStreaming}
              title="Datei hochladen"
              data-testid="upload-btn"
            >
              <Paperclip className="w-4 h-4 text-slate-400" />
            </Button>
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Nachricht an den Assistenten..."
                className="w-full resize-none rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-300 min-h-[44px] max-h-[120px]"
                rows={1}
                disabled={isStreaming}
                data-testid="chat-input"
              />
            </div>
            <Button
              size="sm"
              className="shrink-0 h-10 w-10 p-0 rounded-xl"
              onClick={() => sendMessage()}
              disabled={isStreaming || (!input.trim() && !uploadFile)}
              data-testid="send-btn"
            >
              {isStreaming ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
